/**
 * Injects super-admin-configured markup into the dashboard <head>.
 *
 * The customer dashboard SPA fetches the public, unauthenticated
 * `GET /v1/config/dashboard-scripts` payload on load (before React renders)
 * and injects the configured raw HTML — typically <script> tags for the
 * Bruno widget and/or analytics — into `document.head`. This runs on every
 * dashboard page, including the unauthenticated login/signup pages.
 *
 * Crucially, `<script>` elements inserted via `innerHTML` are inert (the HTML
 * spec disables them), so we recreate each script element imperatively so it
 * actually executes. Non-script elements (link/meta/style) are cloned as-is.
 *
 * This is intentionally NOT used by the super-admin app (apps/admin).
 */
import type { PublicDashboardScriptsDTO } from '@bruno/shared';
import { API_URL } from '../config';

const MARKER_ATTR = 'data-bruno-injected';

let started = false;

function materializeNode(node: Node): Node | null {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as Element;
    if (el.tagName === 'SCRIPT') {
      // Recreate the script so the browser executes it (innerHTML-parsed
      // scripts never run). Copy every attribute (src, async, defer, type,
      // data-*, ...) plus any inline body.
      const script = document.createElement('script');
      for (const attr of Array.from(el.attributes)) {
        script.setAttribute(attr.name, attr.value);
      }
      if (el.textContent) script.textContent = el.textContent;
      return script;
    }
    return el.cloneNode(true);
  }
  if (node.nodeType === Node.COMMENT_NODE) {
    return node.cloneNode(true);
  }
  // Ignore stray whitespace/text nodes between elements.
  return null;
}

export async function injectDashboardScripts(): Promise<void> {
  if (started) return;
  if (typeof document === 'undefined') return;
  // Guard against double-injection (e.g. HMR re-evaluating the module).
  if (document.querySelector(`[${MARKER_ATTR}]`)) {
    started = true;
    return;
  }
  started = true;

  let cfg: PublicDashboardScriptsDTO;
  try {
    const res = await fetch(`${API_URL}/v1/config/dashboard-scripts`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return;
    cfg = (await res.json()) as PublicDashboardScriptsDTO;
  } catch {
    // Network/parse failure: never block the dashboard from loading.
    return;
  }

  if (!cfg.enabled || !cfg.headHtml.trim()) return;

  const template = document.createElement('template');
  template.innerHTML = cfg.headHtml;

  for (const node of Array.from(template.content.childNodes)) {
    const el = materializeNode(node);
    if (!el) continue;
    if (el.nodeType === Node.ELEMENT_NODE) {
      (el as Element).setAttribute(MARKER_ATTR, '');
    }
    document.head.appendChild(el);
  }
}
